import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Gift, Copy, Check } from 'lucide-react';
import { RootState } from '../../../redux/rootReducer';
import UserService from '../../../services/user.service';

function ConfettiDot({ className }: { className: string }) {
    return <span className={`absolute rounded-sm ${className}`} aria-hidden />;
}

function GiftIllustration() {
    return (
        <div className="relative mx-auto h-[180px] w-[180px] sm:h-[200px] sm:w-[200px] lg:mx-0 lg:h-[220px] lg:w-[220px]">
            {/* Confetti */}
            <ConfettiDot className="left-[8%] top-[12%] h-2.5 w-2.5 rotate-12 bg-[#FF8A5B]" />
            <ConfettiDot className="left-[22%] top-[4%] h-2 w-2 rotate-45 bg-[#F5C542]" />
            <ConfettiDot className="right-[18%] top-[10%] h-2.5 w-2.5 -rotate-12 bg-[#E05AAE]" />
            <ConfettiDot className="right-[6%] top-[28%] h-2 w-3 rotate-[20deg] bg-[#AA468F]" />
            <ConfettiDot className="left-[4%] top-[40%] h-2 w-2.5 -rotate-45 bg-[#7DD3FC]" />
            <ConfettiDot className="right-[12%] bottom-[22%] h-2.5 w-2 rotate-12 bg-[#F5C542]" />
            <ConfettiDot className="left-[14%] bottom-[18%] h-2 w-2 rotate-45 bg-[#E05AAE]" />
            <ConfettiDot className="left-[38%] top-[8%] h-1.5 w-1.5 rounded-full bg-white/70" />
            <ConfettiDot className="right-[30%] top-[18%] h-1.5 w-1.5 rounded-full bg-[#FF8A5B]" />

            {/* Soft glow */}
            <div className="absolute inset-[18%] rounded-full bg-ryd-primary/40 blur-2xl" />

            {/* Gift box */}
            <div className="absolute inset-[18%] flex flex-col items-center justify-end">
                {/* Lid / bow area */}
                <div className="relative z-10 mb-[-6px] flex w-[78%] flex-col items-center">
                    <div className="mb-1 flex items-end gap-0">
                        <div className="h-5 w-5 rounded-tl-full border-[3px] border-b-0 border-r-0 border-[#F5C542] opacity-90" />
                        <div className="h-5 w-5 rounded-tr-full border-[3px] border-b-0 border-l-0 border-[#F5C542] opacity-90" />
                    </div>
                    <div className="h-4 w-full rounded-t-md bg-gradient-to-b from-[#C86AA3] to-[#AA468F] shadow-md" />
                    <div className="absolute left-1/2 top-3 h-5 w-3 -translate-x-1/2 rounded-sm bg-[#F5C542]" />
                </div>

                {/* Box body */}
                <div className="relative flex h-[88px] w-[78%] items-center justify-center rounded-b-xl bg-gradient-to-br from-[#AA468F] via-[#9c2788] to-[#6B1F66] shadow-[0_18px_40px_rgba(0,0,0,0.35)] sm:h-[100px]">
                    <div className="absolute inset-y-0 left-1/2 w-3 -translate-x-1/2 bg-[#F5C542]" />
                    <div className="absolute inset-x-0 top-[42%] h-3 bg-[#F5C542]/90" />
                    <Gift size={34} className="relative z-10 text-white/90" strokeWidth={1.8} />
                </div>
            </div>
        </div>
    );
}

export const ReferralSection = () => {
    const userInfo: any = useSelector((state: RootState) => state.auth.userInfo);
    const [isCopied, setIsCopied] = useState(false);
    const [ref, setRef] = useState(0);
    const userService = new UserService();
    const referralLink = `https://parent.rydlearning.com/parent/sign-up?invite=ryd${userInfo?.id + 100}`;
    const hasFreeCohort = userInfo?.freeCohort > 0;

    useEffect(() => {
        if (userInfo?.id) getReferedParents(userInfo.id);
    }, [userInfo]);

    const getReferedParents = async (id: any) => {
        try {
            const response = await userService.getReferedParents(id);
            if (!response.status) return;
            setRef(response.data.no);
        } catch {
            /* ignore */
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(referralLink);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    return (
        <div className="relative overflow-hidden rounded-[22px] bg-[#1B1530] p-6 shadow-[0_20px_60px_rgba(27,21,48,0.35)] sm:p-8 lg:p-10">
            {/* Subtle ambient lights like the mock */}
            <div className="pointer-events-none absolute -left-10 top-0 h-40 w-40 rounded-full bg-ryd-primary/20 blur-3xl" />
            <div className="pointer-events-none absolute bottom-0 right-0 h-48 w-48 rounded-full bg-[#E05AAE]/15 blur-3xl" />

            <div className="relative z-10 grid items-center gap-8 lg:grid-cols-[1fr_auto]">
                <div className="min-w-0">
                    <h2 className="font-[AvertaStd-Semibold] text-[24px] leading-tight text-white sm:text-[28px]">
                        Invite Friends &amp; Earn Rewards
                    </h2>
                    <p className="mt-2 max-w-xl text-[14px] leading-relaxed text-white/65 sm:text-[15px]">
                        Share your referral link with other parents and get exclusive benefits
                    </p>

                    <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                        <p className="text-[13px] text-white/80 sm:text-[14px]">
                            {hasFreeCohort ? (
                                <>
                                    Referral Incentive:{' '}
                                    <span className="font-semibold text-emerald-300">
                                        Reward unlocked — {userInfo?.freeCohort} free cohort
                                    </span>
                                </>
                            ) : (
                                <>
                                    Referral Incentive:{' '}
                                    <span className="text-white/70">Invite 3 parents to unlock a free Cohort</span>
                                </>
                            )}
                        </p>
                        <p className="shrink-0 text-[14px] font-semibold text-white">
                            {ref} parents
                        </p>
                    </div>

                    <div className="mt-4 flex flex-col overflow-hidden rounded-[12px] border border-white/10 bg-[#12101F]/90 sm:flex-row">
                        <input
                            type="text"
                            value={referralLink}
                            readOnly
                            className="min-w-0 flex-1 truncate bg-transparent px-4 py-3.5 text-[13px] text-white/75 outline-none"
                        />
                        <button
                            type="button"
                            onClick={handleCopy}
                            className={`inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[13px] font-semibold text-white transition sm:min-w-[120px] ${
                                isCopied ? 'bg-emerald-600' : 'bg-ryd-primary hover:bg-[#C86AA3]'
                            }`}
                        >
                            {isCopied ? <Check size={16} /> : <Copy size={16} />}
                            {isCopied ? 'Copied!' : 'Copy Link'}
                        </button>
                    </div>

                    <p className="mt-4 text-center text-[13px] text-white/55 sm:text-left">
                        Invite Friends &amp; Earn a Free Cohort!
                    </p>
                </div>

                <div className="flex justify-center lg:justify-end lg:pl-4">
                    <GiftIllustration />
                </div>
            </div>
        </div>
    );
};
